/**
 * Shared MediaStreamTrack helpers for call capture + in-call restore.
 * Safe to call on ended/detached tracks; never throws to the caller.
 */

export function stopTrackQuietly(track) {
  if (!track?.stop) return;
  try {
    track.stop();
  } catch (_) { }
}

export function pruneEndedTracks(stream) {
  if (!stream?.getTracks) return;
  stream.getTracks().forEach((track) => {
    if (track?.readyState !== 'ended') return;
    try {
      stream.removeTrack(track);
    } catch (_) { }
  });
}

export function stopStreamTracksExcept(stream, keepTrack) {
  if (!stream?.getTracks) return;
  stream.getTracks().forEach((track) => {
    if (keepTrack && track?.id === keepTrack.id) return;
    stopTrackQuietly(track);
  });
}

export function streamHasLiveAudio(stream) {
  return (stream?.getAudioTracks?.() ?? []).some((track) => track.readyState === 'live');
}

export function streamHasLiveVideo(stream) {
  return (stream?.getVideoTracks?.() ?? []).some((track) => track.readyState === 'live');
}

export function findLiveVideoTrack(stream) {
  return (stream?.getVideoTracks?.() ?? []).find((track) => track.readyState === 'live') ?? null;
}

export function getLiveOrFirstVideoTrack(stream) {
  const tracks = stream?.getVideoTracks?.() ?? [];
  return tracks.find((track) => track.readyState === 'live') ?? tracks[0] ?? null;
}

export function getVideoDeviceIdFromStream(stream) {
  return getLiveOrFirstVideoTrack(stream)?.getSettings?.()?.deviceId ?? null;
}

export function getVideoFacingModeFromStream(stream) {
  return getLiveOrFirstVideoTrack(stream)?.getSettings?.()?.facingMode ?? null;
}

/**
 * True when the track can satisfy `exactDeviceId`.
 * Missing settings.deviceId is not a match — re-acquire rather than reuse the
 * wrong camera after a flip.
 */
export function videoTrackMatchesDeviceId(track, exactDeviceId) {
  if (!track) return false;
  if (!exactDeviceId) return true;
  const deviceId = track.getSettings?.()?.deviceId;
  if (!deviceId) return false;
  return deviceId === exactDeviceId;
}

export function videoTrackMatchesFacingMode(track, facingMode) {
  if (!track) return false;
  if (!facingMode) return true;
  const actual = track.getSettings?.()?.facingMode;
  if (!actual) return false;
  return actual === facingMode;
}

/**
 * True when this live track is the camera the caller asked for.
 * Match on deviceId OR facingMode so iPhone's multiple back sensors (wide /
 * ultra-wide) are accepted after a flip. Proven mismatches are rejected.
 */
export function videoTrackSatisfiesRequest(track, exactDeviceId, facingMode) {
  if (!track) return false;
  if (!exactDeviceId && !facingMode) return true;

  const settings = track.getSettings?.() ?? {};
  if (exactDeviceId && settings.deviceId === exactDeviceId) return true;
  if (facingMode && settings.facingMode === facingMode) return true;
  // Browser omitted settings on a newly opened track — cannot prove a mismatch.
  if (!settings.deviceId && !settings.facingMode) return true;
  return false;
}

export function attachTrackToStream(stream, track) {
  if (!stream || !track) return false;
  pruneEndedTracks(stream);
  const alreadyHasTrack = (stream.getTracks?.() ?? []).some((item) => item.id === track.id);
  if (alreadyHasTrack) return true;
  try {
    stream.addTrack(track);
    return true;
  } catch (_) {
    return false;
  }
}

/** Swap in a new camera without leaving the previous video track on the stream. */
export function replaceVideoTrackOnStream(stream, track) {
  if (!stream || !track) return false;
  pruneEndedTracks(stream);
  (stream.getVideoTracks?.() ?? []).forEach((existing) => {
    if (!existing || existing.id === track.id) return;
    try {
      stream.removeTrack(existing);
    } catch (_) { }
    if (existing.readyState === 'live') stopTrackQuietly(existing);
  });
  return attachTrackToStream(stream, track);
}
